import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import { accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount } from '../lib/accountStorage';
import type { GpbEnrollment } from '../utils/gpb/enrollment';
import { enroll as makeEnrollment, openNextRank } from '../utils/gpb/enrollment';
import { mergeEnrolled } from '../utils/gpb/mergeEnrollment';
import type { GpbBinding } from '../utils/gpb/readEvidence';
import type { GpbDefinition } from '../types/gpb';

/**
 * ⭐⭐ **WHAT THE PERSON HAS TAKEN OFF THE SHELF.** A profile is a catalogue entry
 * until something here records that they took it (D15), so every other surface
 * reads this: which pathways are in the library, which rank each is on, what its
 * authored ids bound to, and the version it was taken at.
 *
 * ⚠️⚠️ **EVERY RECORD AN APPLY CREATES SYNCS ON ITS OWN — ONLY THE BINDING DID NOT.**
 * Habits, projects, abilities and programs each carry themselves to the cloud, so
 * before `gpb_enrollments` (mig 0270) a person who cleared three ranks on a laptop
 * opened their phone to an empty library sitting beside two dozen records they
 * never made. The orphans were the bug, not the missing progress.
 *
 * ⚠️⚠️ **THE ROW IS MERGED, NEVER OVERWRITTEN** — see `mergeEnrollment`. Two devices
 * each applying a different quest must keep both sets of bindings, or the sync
 * meant to close that hole reopens it.
 */

export interface GpbEnrolled {
  /**
   * ⚠️⚠️ **THE BINDING LIVES ON THE ENROLLMENT, AND ONLY THERE.** This wrapper used
   * to carry its own copy — two homes for one fact, which is how two doors onto a
   * record start disagreeing. `enrollment.binding` is the single one.
   */
  enrollment: GpbEnrollment;
  /** ⚠️ D34: set aside is a LABEL. Nothing is removed, no window expires. */
  aside?: boolean;
  /**
   * ⚠️ D31: the only deadline in the system, and the person wrote it. The profile
   * never sets one. Missing it costs nothing — it is said out loud, not enforced.
   */
  commitment?: { rank: number; weeks: number; setOn: string };
  /**
   * ⭐⭐ D32 — the person's own four-part plan for coming back, in their words.
   *
   * ⚠️⚠️ **PARTIAL IS A REAL STATE, ON PURPOSE.** Unlike `commitment`, which is all
   * three or nothing because a commitment with a missing part cannot be judged,
   * this is WRITING: three parts of four is kept, because throwing away what
   * somebody typed for not having finished typing is exactly the "box that
   * forgets" this replaced. Anything written carries `savedOn`.
   */
  returnPlan?: GpbReturnPlan;
}

/**
 * ⚠️ Every part optional; `savedOn` is not, and the table refuses a written part
 * with no date. It is also the key the two-device merge compares.
 */
export interface GpbReturnPlan {
  trigger?: string;
  action?: string;
  opportunity?: string;
  follows?: string;
  /** Local `YYYY-MM-DD` of the most recent save. */
  savedOn: string;
}

const TABLE = 'gpb_enrollments';

interface Row {
  definition_id: string; version: string; timezone: string;
  commitment: GpbEnrollment['commitment'];
  status: GpbEnrollment['status'];
  windows: Record<string, string> | null;
  binding: Partial<GpbBinding> | null;
  aside: boolean;
  commitment_rank: number | null;
  commitment_weeks: number | null;
  commitment_set_on: string | null;
  return_trigger: string | null;
  return_action: string | null;
  return_opportunity: string | null;
  return_follows: string | null;
  return_saved_on: string | null;
}

function rowToEnrolled(row: Row): GpbEnrolled {
  return {
    enrollment: {
      id: row.definition_id,
      definitionId: row.definition_id,
      version: row.version,
      timezone: row.timezone,
      commitment: row.commitment,
      status: row.status,
      windows: Object.fromEntries(
        Object.entries(row.windows ?? {}).map(([rank, date]) => [Number(rank), date]),
      ),
      /* ⚠️ Channel by channel, never a spread over defaults: a row whose JSON is
         missing a channel must read as EMPTY, not undefined — every consumer
         indexes these without checking. */
      binding: {
        quests: row.binding?.quests ?? {},
        habits: row.binding?.habits ?? {},
        skills: row.binding?.skills ?? {},
        projects: row.binding?.projects ?? {},
        modules: row.binding?.modules ?? {},
      },
    },
    aside: row.aside,
    commitment:
      row.commitment_rank !== null && row.commitment_weeks !== null && row.commitment_set_on !== null
        ? { rank: row.commitment_rank, weeks: row.commitment_weeks, setOn: row.commitment_set_on }
        : undefined,
    /* ⚠️ `savedOn` is what makes a plan EXIST, not any particular part — a row with
       three parts written is a real arrangement, and one with none is absent. */
    returnPlan: row.return_saved_on !== null
      ? {
          trigger: row.return_trigger ?? undefined,
          action: row.return_action ?? undefined,
          opportunity: row.return_opportunity ?? undefined,
          follows: row.return_follows ?? undefined,
          savedOn: row.return_saved_on,
        }
      : undefined,
  };
}

function enrolledToRow(at: GpbEnrolled, userId: string) {
  return {
    user_id: userId,
    definition_id: at.enrollment.definitionId,
    version: at.enrollment.version,
    timezone: at.enrollment.timezone,
    commitment: at.enrollment.commitment,
    status: at.enrollment.status,
    windows: at.enrollment.windows,
    binding: at.enrollment.binding,
    aside: at.aside ?? false,
    /* ⚠️ All three or none — the table refuses anything else, because a commitment
       with no date cannot be judged and a date with no length is not one. */
    commitment_rank: at.commitment?.rank ?? null,
    commitment_weeks: at.commitment?.weeks ?? null,
    commitment_set_on: at.commitment?.setOn ?? null,
    /* ⚠️ An empty string is NOT a written part — the column's length check refuses
       it, and "written but blank" would be a third state every reader must handle.
       Trimmed to null here so the refusal never reaches the network. */
    return_trigger: blankToNull(at.returnPlan?.trigger),
    return_action: blankToNull(at.returnPlan?.action),
    return_opportunity: blankToNull(at.returnPlan?.opportunity),
    return_follows: blankToNull(at.returnPlan?.follows),
    return_saved_on: at.returnPlan?.savedOn ?? null,
  };
}

/** ⚠️ Whitespace is not writing. `'  '` must reach the column as NULL, not fail it. */
const blankToNull = (v: string | undefined): string | null => {
  const t = v?.trim();
  return t ? t : null;
};

/** ⚠️ Write-through, fire-and-forget: a failed push must never block the person,
    and the next `initialize` MERGES rather than overwrites, so a missed write is
    recovered on the next boot instead of lost. */
async function cloudUpsert(list: readonly GpbEnrolled[]): Promise<void> {
  if (list.length === 0) return;
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return;
    const { error } = await supabase
      .from(TABLE)
      .upsert(list.map(at => enrolledToRow(at, user.id)), { onConflict: 'user_id,definition_id' });
    if (error) console.error('Failed to sync GPB enrollment:', error);
  } catch (error) {
    console.error('Error syncing GPB enrollment:', error);
  }
}

const EMPTY_BINDING: GpbBinding = { quests: {}, habits: {}, skills: {}, projects: {}, modules: {} };

interface GpbState {
  enrolled: Record<string, GpbEnrolled>;
  initializedUserId: string | null;
  cachedUserId: string | null;
  initialize: () => Promise<void>;
  take: (def: GpbDefinition, today: string, timezone: string,
         commitment?: GpbEnrollment['commitment']) => void;
  saveBinding: (definitionId: string, binding: GpbBinding) => void;
  advanceTo: (def: GpbDefinition, rank: number, completedOn: string) => void;
  /**
   * ⚠️⚠️ **RESTARTING MOVES THE RANK'S WINDOW TO `on`, AND THE CALLER MUST HAVE
   * CHECKED THAT NOTHING IN THE RANK IS MET.** Every repetition goal counts from
   * that window, so moving it forward un-counts every day before it — which is
   * correct for a rank nobody has finished anything in, and would be a theft of
   * finished work otherwise. D30: a restart can never take back a goal already
   * met, and nothing here can tell whether one has been. The store cannot judge
   * that; `gpbProgress` can, and does.
   */
  restartRank: (definitionId: string, rank: number, on: string) => void;
  /**
   * ⏳⏳ **NO CALLER, ON PURPOSE — IT IS WAITING FOR A SECOND PROFILE, NOT FOR
   * SOMEBODY TO REMEMBER IT.** Do not delete this as dead code.
   *
   * Set aside is D34's gentle option on the SWITCHING surface, and that surface
   * exists for one reason the record is specific about: two profiles at full
   * commitment claim **eighteen hours a day**, before sleep, work and everything
   * the person never put in a profile. Exactly one profile ships today, so nobody
   * can hold two, so nothing can need setting aside. The precondition is a second
   * profile — which the builder now makes imminent, not hypothetical.
   *
   * ⚠️⚠️ **IT IS A LABEL AND NOTHING ELSE (D34).** Setting a profile aside archives
   * no habit, deletes no project, removes no skill and touches no arranged day.
   * Counts stay, cleared ranks stay, and no window expires because there are no
   * dates to run out (D21). Picked up a month later it is exactly where it was —
   * verified in the prototype, 4 / 7 before and 4 / 7 after.
   * ▶ The record names tidying away an aside profile's records as *"the worst bug
   * in the feature"*: it would feel like good housekeeping and would break D34's
   * one rule that must not bend. Eating well is not Athlete's property.
   *
   * ▶ **Already built, so whoever adds the surface does not rebuild it:** this
   * action, the `aside` column (mig 0270) and the merge rule in `mergeEnrollment`
   * — where the SERVER's answer wins, because putting a profile away is a decision
   * about the person, not about the device they did it on.
   * ▶ **Still missing:** the control, and the library badge that reads it.
   */
  setAside: (definitionId: string, aside: boolean) => void;
  setCommitment: (definitionId: string, rank: number, weeks: number, on: string) => void;
  clearCommitment: (definitionId: string) => void;
  /**
   * ⚠️ D32. Saves whatever has been written — a partial arrangement is kept, not
   * refused. Passing every part blank CLEARS it rather than storing an empty
   * record with a date, because "saved, but nothing in it" is not a state the
   * panel should have to explain.
   */
  saveReturnPlan: (definitionId: string, plan: Omit<GpbReturnPlan, 'savedOn'>, on: string) => void;
  of: (definitionId: string) => GpbEnrolled | undefined;
}

export const useGpbStore = create<GpbState>()(
  persist(
    (set, get) => {
      const push = (id: string) => {
        const at = get().enrolled[id];
        if (at) void cloudUpsert([at]);
      };

      return {
        enrolled: {},
        initializedUserId: null,
        cachedUserId: null,

        /**
         * ⚠️⚠️ MERGE, never replace. The local row may hold bindings this device
         * made offline; the cloud row may hold bindings another device made.
         * Taking either side whole strands the other's records.
         */
        initialize: () => dedupeInit('gpb', async () => {
          const at = accountGeneration();
          try {
            const { user } = await syncManager.safeAuthCheck();
            if (!user) return;
            if (get().initializedUserId === user.id) return;
            if (get().cachedUserId === user.id && isCloudFresh('gpb', user.id)) {
              set({ initializedUserId: user.id });
              return;
            }

            const { data, error } = await supabase.from(TABLE).select('*');
            /* ⚠️⚠️ An answer for an account that has since signed out is not this copy's — and the
               push-back below would write it AS the account signed in now. */
            if (at !== accountGeneration()) return;
            if (error) throw error;

            set(state => {
              /* ⚠️ A cache belonging to somebody else is DROPPED, not merged —
                 signing in as a second person must not inherit the first's
                 pathways. */
              const ours = state.cachedUserId === null || state.cachedUserId === user.id;
              const merged: Record<string, GpbEnrolled> = ours ? { ...state.enrolled } : {};
              for (const row of (data ?? []) as Row[]) {
                merged[row.definition_id] = mergeEnrolled(merged[row.definition_id], rowToEnrolled(row));
              }
              return { enrolled: merged, initializedUserId: user.id, cachedUserId: user.id };
            });
            markCloudFresh('gpb', user.id);

            /* ⚠️ Push the merge back: what this device contributed is not on the
               server until it is written, and the next device must see it. */
            void cloudUpsert(Object.values(get().enrolled));
          } catch (error) {
            console.error('Failed to load GPB enrollments:', error);
          }
        }),

        /**
         * ⚠️ Taking a profile creates NOTHING in the account (D15/D33) — it adds a
         * row here and nothing else. Records appear only when a quest is applied.
         * ⚠️ Idempotent: taking one already taken must not reset its rank or
         * binding.
         */
        take: (def, today, timezone, commitment = 'full') => {
          if (get().enrolled[def.id]) return;
          set(state => ({
            enrolled: {
              ...state.enrolled,
              [def.id]: {
                enrollment: makeEnrollment(def, today, EMPTY_BINDING, {
                  id: crypto.randomUUID(), timezone, commitment,
                }),
              },
            },
          }));
          push(def.id);
        },

        /**
         * ⚠️⚠️ Saved after EVERY create, not at the end of an Apply — three stores
         * sync independently, and a binding written once at the end strands every
         * record already made if the app closes halfway.
         */
        saveBinding: (definitionId, binding) => {
          set(state => {
            const at = state.enrolled[definitionId];
            if (!at) return state;
            return { enrolled: { ...state.enrolled,
              [definitionId]: { ...at, enrollment: { ...at.enrollment, binding } } } };
          });
          push(definitionId);
        },

        /**
         * ⚠️⚠️ `completedOn` is the day the LAST requirement was met, never the day
         * the app noticed (D28) — the next window is derived from it, so somebody
         * who reopens a fortnight later does not have every later rank shifted.
         */
        advanceTo: (def, rank, completedOn) => {
          set(state => {
            const at = state.enrolled[def.id];
            if (!at) return state;
            return { enrolled: { ...state.enrolled,
              [def.id]: { ...at, enrollment: openNextRank(at.enrollment, rank, completedOn, def) } } };
          });
          push(def.id);
        },

        restartRank: (definitionId, rank, on) => {
          set(state => {
            const at = state.enrolled[definitionId];
            if (!at) return state;
            return { enrolled: { ...state.enrolled, [definitionId]: { ...at,
              enrollment: { ...at.enrollment,
                windows: { ...at.enrollment.windows, [rank]: on } } } } };
          });
          push(definitionId);
        },

        saveReturnPlan: (definitionId, plan, on) => {
          set(state => {
            const at = state.enrolled[definitionId];
            if (!at) return state;
            const kept = {
              trigger: plan.trigger?.trim() || undefined,
              action: plan.action?.trim() || undefined,
              opportunity: plan.opportunity?.trim() || undefined,
              follows: plan.follows?.trim() || undefined,
            };
            /* ⚠️ Nothing written at all is an ABSENT arrangement, not an empty one
               stamped with today — the table's own guard says a date without a part
               is a record nobody can place. */
            const any = Object.values(kept).some(Boolean);
            const returnPlan = any ? { ...kept, savedOn: on } : undefined;
            return { enrolled: { ...state.enrolled, [definitionId]: { ...at, returnPlan } } };
          });
          push(definitionId);
        },

        /* ⏳ Uncalled until a second profile exists — see the declaration above,
           and D34. Not dead code. */
        setAside: (definitionId, aside) => {
          set(state => {
            const at = state.enrolled[definitionId];
            if (!at) return state;
            return { enrolled: { ...state.enrolled, [definitionId]: { ...at, aside } } };
          });
          push(definitionId);
        },

        setCommitment: (definitionId, rank, weeks, on) => {
          set(state => {
            const at = state.enrolled[definitionId];
            if (!at) return state;
            return { enrolled: { ...state.enrolled,
              [definitionId]: { ...at, commitment: { rank, weeks, setOn: on } } } };
          });
          push(definitionId);
        },

        clearCommitment: definitionId => {
          set(state => {
            const at = state.enrolled[definitionId];
            if (!at) return state;
            const { commitment: _drop, ...rest } = at;
            return { enrolled: { ...state.enrolled, [definitionId]: rest } };
          });
          push(definitionId);
        },

        of: definitionId => get().enrolled[definitionId],
      };
    },
    {
      name: 'metanoia-gpb',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      partialize: state => ({ enrolled: state.enrolled, cachedUserId: state.cachedUserId }),
    },
  ),
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: the next account saw the last
   one's pathways until its own load, and its first edit before that load pushed them into its own
   rows; an enrollment whose write never landed was replaced by the next account's load; and the next
   account's enrollment made before its own load was merged into the last one's by ITS next load —
   and pushed back into its rows. */
scopeToAccount(useGpbStore);
